import { useState, useEffect } from 'react'
import { Outlet, Link, useParams, useLocation, useMatches } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { Logo } from '../../components/branding/Logo'
import { Footer } from '../landing/LandingPage'
import { type WPCategory } from '../../api/wordpress'

export function BlogLayout() {
  const [isOpen, setIsOpen] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [mounted, setMounted] = useState(false)

  const matches = useMatches()
  const categories: WPCategory[] = matches.flatMap(
    m => ((m.data as Record<string, unknown> | null)?.categories as WPCategory[] | undefined) ?? []
  )
  const { categorySlug } = useParams<{ categorySlug?: string }>()
  const location = useLocation()
  const isAllPosts = location.pathname === '/blog' && !categorySlug

  useEffect(() => {
    setMounted(true)
    setIsAuthenticated(!!localStorage.getItem('token'))
  }, [])

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <Logo className="h-10" />
            </Link>

            {/* Desktop category nav — client-only to prevent hydration mismatch */}
            {mounted && categories.length > 0 && (
              <div className="hidden lg:flex items-center gap-6">
                <Link
                  to="/blog"
                  className={`transition-colors font-medium text-sm ${
                    isAllPosts
                      ? 'text-primary font-semibold'
                      : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  All Posts
                </Link>
                {categories.slice(0, 4).map(category => (
                  <Link
                    key={category.id}
                    to={`/blog/category/${category.slug}`}
                    className={`text-sm transition-colors ${
                      categorySlug === category.slug
                        ? 'text-primary font-semibold'
                        : 'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    {category.name}
                  </Link>
                ))}
              </div>
            )}

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-4">
              {mounted && isAuthenticated ? (
                <Link
                  to="/app/home"
                  className="bg-primary text-text-inverse px-5 py-2.5 rounded-full font-medium text-sm hover:bg-primary-hover transition-colors"
                >
                  Go to App
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-text-primary font-medium hover:text-primary transition-colors text-sm"
                  >
                    Log in
                  </Link>
                  <Link
                    to="/create"
                    className="btn btn-primary text-sm"
                  >
                    Create Your Invite
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu button — client-only to prevent hydration mismatch */}
            {mounted && (
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="md:hidden p-2 text-text-primary"
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            )}
          </div>

          {/* Mobile menu */}
          {mounted && isOpen && (
            <div className="md:hidden py-4 border-t border-border">
              <div className="flex flex-col gap-4">
                <Link
                  to="/blog"
                  onClick={() => setIsOpen(false)}
                  className="text-text-primary font-medium hover:text-primary transition-colors"
                >
                  All Posts
                </Link>
                {categories.map(category => (
                  <Link
                    key={category.id}
                    to={`/blog/category/${category.slug}`}
                    onClick={() => setIsOpen(false)}
                    className="text-text-secondary hover:text-primary transition-colors"
                  >
                    {category.name}
                  </Link>
                ))}
                {isAuthenticated ? (
                  <Link
                    to="/app/home"
                    onClick={() => setIsOpen(false)}
                    className="bg-primary text-text-inverse px-5 py-2.5 rounded-full font-medium text-sm text-center hover:bg-primary-hover transition-colors"
                  >
                    Go to App
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setIsOpen(false)}
                      className="text-text-primary font-medium hover:text-primary transition-colors"
                    >
                      Log in
                    </Link>
                    <Link
                      to="/create"
                      onClick={() => setIsOpen(false)}
                      className="bg-primary text-text-inverse px-5 py-2.5 rounded-full font-medium text-sm text-center hover:bg-primary-hover transition-colors"
                    >
                      Create Your Invite
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">
        <Outlet />
      </main>

      <Footer />
    </div>
  )
}
